import { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Layout, Menu, Avatar, Dropdown, Typography, Badge, Drawer, Button, Grid, Tooltip } from 'antd';
import {
  CloudOutlined,
  ProfileOutlined,
  MenuOutlined,
  MobileOutlined,
  DashboardOutlined,
  ProjectOutlined,
  CalendarOutlined,
  VideoCameraOutlined,
  FolderOutlined,
  MessageOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  BarChartOutlined,
  DollarOutlined,
  ApartmentOutlined,
  MailOutlined,
  TeamOutlined,
  LogoutOutlined,
  UserOutlined,
  ExclamationCircleOutlined,
  CustomerServiceOutlined,
  PictureOutlined,
  SearchOutlined,
  BellOutlined,
  SettingOutlined,
  CloudServerOutlined,
} from '@ant-design/icons';
import { useAuth } from '../store/auth';
import { useNotifications } from '../store/NotificationContext';
import GlobalSearch from '../components/GlobalSearch';
import api from '../api/client';

const { Header, Sider, Content } = Layout;
const { useBreakpoint } = Grid;

export default function MainLayout() {
  const screens = useBreakpoint();
  const isMobile = !screens.lg;
  const [collapsed, setCollapsed] = useState(false);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { unreadChatCount } = useNotifications();
  const [pendingBpmCount, setPendingBpmCount] = useState(0);
  const [apkVersion, setApkVersion] = useState(null);
  const [appVersion, setAppVersion] = useState(null);

  useEffect(() => {
    fetchPendingCount();
    // Версия Android-приложения для кнопки «Скачать APK»
    fetch('/apk/version.json')
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => v && setApkVersion(v))
      .catch(() => {});
    // Версия системы для сайдбара (источник — файл VERSION, эндпоинт /api/v1/system/version)
    fetch('/api/v1/system/version')
      .then((r) => (r.ok ? r.json() : null))
      .then((v) => v?.version && setAppVersion(v.version))
      .catch(() => {});
  }, []);

  const fetchPendingCount = async () => {
    try {
      const res = await api.get('/bpm/pending');
      setPendingBpmCount(res.data?.length || 0);
    } catch {
      // ignore
    }
  };

  const isGuestUser = Number(user?.role_level) === 0;
  const menuItems = isGuestUser ? [
    {
      key: '/access',
      icon: <ProfileOutlined />,
      label: 'Мои доступы'
    },
    {
      key: '/talk',
      icon: <CloudOutlined style={{ color: '#0082c9' }} />,
      label: 'Чат'
    },
  ] : [
    { 
      key: '/', 
      icon: <DashboardOutlined />, 
      label: 'Главная' 
    },
    { 
      key: '/feed', 
      icon: <PictureOutlined />, 
      label: 'Лента' 
    },
    { 
      key: '/activity', 
      icon: <Badge count={pendingBpmCount > 0 ? pendingBpmCount : undefined} size="small" offset={[5, 0]}>
        <ExclamationCircleOutlined />
      </Badge>, 
      label: 'Действия' 
    },
    { 
      key: '/projects', 
      icon: <ProjectOutlined />, 
      label: 'Проекты' 
    },
    { 
      key: '/tasks', 
      icon: <ProjectOutlined />, 
      label: 'Задачи' 
    },
    { 
      key: 'calendar_group', 
      icon: <CalendarOutlined />, 
      label: 'Календарь',
      children: [
        { key: '/calendar', label: 'Календарь', icon: <CalendarOutlined /> },
        { key: '/regular-meetings', label: 'Регулярные', icon: <ClockCircleOutlined /> },
        { key: '/meetings', label: 'Встречи', icon: <VideoCameraOutlined /> },
      ]
    },
    {
      key: 'nc-external',
      icon: <CloudServerOutlined />,
      label: <a href="https://opp.spzvg.ru" target="_blank" rel="noopener noreferrer">Nextcloud</a>,
    },
    { 
      key: '/chat', 
      icon: <Badge count={unreadChatCount} size="small" offset={[5, 0]}>
        <MessageOutlined />
      </Badge>, 
      label: 'С.Чат' 
    },
    { 
      key: '/talk', 
      icon: <CloudOutlined style={{ color: '#0082c9' }} />, 
      label: 'Чат' 
    },
    { key: '/mail', icon: <MailOutlined />, label: 'Почта' },
    { key: '/reports', icon: <FileTextOutlined />, label: 'Отчёты' },
    { 
      key: 'management_group', 
      icon: <ApartmentOutlined />, 
      label: 'Управление',
      children: [
        ...(user?.role_level >= 3 ? [
          {
            key: 'ksg_group',
            icon: <BarChartOutlined />,
            label: 'КСГ',
            children: [
              { key: '/ksg', label: 'Объекты' },
              { key: '/ksg/wizard', label: 'Создать КСГ' },
              { key: '/ksg/drafts', label: 'Черновики' },
            ],
          },
          { key: '/fao', icon: <DollarOutlined />, label: 'ФАО' }
        ] : []),
        { key: '/bpm', label: 'Процессы', icon: <ApartmentOutlined /> }
      ]
    },
    { key: '/users', icon: <TeamOutlined />, label: 'Сотрудники' },
    { key: '/support', icon: <CustomerServiceOutlined />, label: 'Поддержка' },
    ...(user?.role_level >= 5 ? [{ key: '/admin', icon: <SettingOutlined />, label: 'Админ-панель' }] : []),
  ];

  const userMenuItems = [
    { 
      key: 'profile', 
      icon: <UserOutlined />, 
      label: (
        <div>
          <div style={{ fontWeight: 600 }}>{user?.full_name}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>{user?.position}</div>
        </div>
      )
    },
    { type: 'divider' },
    { key: 'settings', icon: <SettingOutlined />, label: 'Настройки' },
    { key: 'logout', icon: <LogoutOutlined />, label: 'Выйти', danger: true },
  ];

  const handleUserMenu = ({ key }) => {
    if (key === 'logout') {
      logout();
      navigate('/login');
    } else if (key === 'profile') {
      navigate(`/users?userId=${user?.id}`);
    }
  };

  const sidebarContent = (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <div 
        style={{ 
          height: 72, 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: collapsed ? 'center' : 'flex-start',
          padding: collapsed ? '0' : '0 20px',
          cursor: 'pointer',
          borderBottom: '1px solid rgba(255, 255, 255, 0.08)',
        }} 
        onClick={() => navigate('/')}
      >
        <div style={{ 
          width: 40, 
          height: 40, 
          borderRadius: 10, 
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 20,
          fontWeight: 700,
          color: 'white',
          flexShrink: 0,
        }}>
          {collapsed ? 'ТС' : 'Т'}
        </div>
        {!collapsed && (
          <div style={{ marginLeft: 12 }}>
            <div style={{ color: 'white', fontSize: 18, fontWeight: 700, lineHeight: 1.2 }}>ТЕРРА-СУП</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 11 }}>v{appVersion || '1.3.0'}</div>
          </div>
        )}
      </div>
      
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={({ key }) => {
          navigate(key);
          if (isMobile) setDrawerVisible(false);
        }}
        style={{ 
          flex: 1, 
          borderRight: 0,
          background: 'transparent',
          padding: '12px 8px',
        }}
      />
      
      {/* Кнопка скачивания Android-приложения */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
        {collapsed && !isMobile ? (
          <Tooltip title={apkVersion ? `Скачать APK (v${apkVersion.version})` : 'Скачать APK для Android'} placement="right">
            <Button
              type="text"
              block
              icon={<MobileOutlined style={{ color: '#3ddc84' }} />}
              onClick={() => window.open('/apk/terra-sup.apk', '_blank')}
              href="/apk/terra-sup.apk"
              download
              style={{ height: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            />
          </Tooltip>
        ) : (
          <Button
            block
            href="/apk/terra-sup.apk"
            download
            icon={<MobileOutlined style={{ color: '#3ddc84' }} />}
            style={{
              height: 44,
              background: 'rgba(61, 220, 132, 0.12)',
              border: '1px solid rgba(61, 220, 132, 0.35)',
              color: '#3ddc84',
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            Скачать APK{apkVersion ? ` · v${apkVersion.version}` : ''}
          </Button>
        )}
      </div>
      
      {!collapsed && !isMobile && (
        <div style={{ 
          padding: '16px 20px',
          borderTop: '1px solid rgba(255, 255, 255, 0.08)',
        }}>
          <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 11 }}>
            © 2024 ТЕРРА-СУП
          </div>
        </div>
      )}
    </div>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {!isMobile ? (
        <Sider 
          breakpoint="lg" 
          collapsedWidth={80}
          collapsible 
          collapsed={collapsed} 
          onCollapse={setCollapsed}
          width={260}
          style={{ 
            overflow: 'auto', 
            height: '100vh', 
            position: 'sticky', 
            top: 0, 
            left: 0,
            background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)',
            boxShadow: '4px 0 24px rgba(0, 0, 0, 0.1)',
          }}
          trigger={null}
        >
          {sidebarContent}
        </Sider>
      ) : (
        <Drawer
          title={null}
          placement="left"
          onClose={() => setDrawerVisible(false)}
          open={drawerVisible}
          styles={{ 
            body: { padding: 0, background: 'linear-gradient(180deg, #0f172a 0%, #1e293b 100%)' },
            header: { display: 'none' }
          }}
          width={280}
        >
          {sidebarContent}
        </Drawer>
      )}
      
      <Layout>
        <Header 
          style={{ 
            padding: '0 24px', 
            background: 'rgba(255, 255, 255, 0.8)',
            backdropFilter: 'blur(12px)',
            WebkitBackdropFilter: 'blur(12px)',
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            height: 72,
            borderBottom: '1px solid #e2e8f0',
            position: 'sticky',
            top: 0,
            zIndex: 100,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            {isMobile && (
              <Button 
                type="text" 
                icon={<MenuOutlined style={{ fontSize: 20 }} />} 
                onClick={() => setDrawerVisible(true)} 
              />
            )}
            {!isMobile && (
              <Button 
                type="text" 
                icon={<MenuOutlined style={{ fontSize: 18 }} />} 
                onClick={() => setCollapsed(!collapsed)}
                style={{ color: '#64748b' }}
              />
            )}
          </div>
          
          <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
            <GlobalSearch />
            
            <Tooltip title="Уведомления">
              <Button 
                type="text" 
                icon={<Badge count={unreadChatCount + pendingBpmCount} size="small" offset={[-2, 2]}>
                  <BellOutlined style={{ fontSize: 20 }} />
                </Badge>}
                style={{ color: '#64748b' }}
              />
            </Tooltip>
            
            <Dropdown menu={{ items: userMenuItems, onClick: handleUserMenu }} trigger={['click']}>
              <div style={{ 
                cursor: 'pointer', 
                display: 'flex', 
                alignItems: 'center', 
                gap: 12,
                padding: '6px 12px',
                borderRadius: 12,
                transition: 'all 0.2s',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = '#f1f5f9'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Avatar 
                  src={user?.avatar_file_id ? `/api/v1/files/${user.avatar_file_id}/content` : null}
                  icon={!user?.avatar_file_id && <UserOutlined />}
                  style={{ 
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                    border: '2px solid #e2e8f0',
                  }}
                />
                {!isMobile && (
                  <div style={{ lineHeight: 1.3 }}>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#0f172a' }}>
                      {user?.full_name}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b' }}>
                      {user?.role_level >= 5 ? 'Администратор' : 'Сотрудник'}
                    </div>
                  </div>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>
        
        <Content 
          style={{ 
            margin: isMobile ? 12 : 24, 
            padding: isMobile ? 16 : 24, 
            background: '#ffffff', 
            borderRadius: 16,
            minHeight: 360,
            boxShadow: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
            overflowX: 'hidden',
          }}
          className="page-container"
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
